import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getBoardList, createBoard, deleteBoard, updateBoard, Board } from '../../api';
import Input from '../common/Input';
import Button from '../common/Button';

// --- 스타일 컴포넌트 ---
const Container = styled.div` padding: 2rem; `;
const Title = styled.h2` font-size: 1.5rem; margin-bottom: 1.5rem; color: var(--text-bright); `;
const BoardGrid = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; `;
const BoardCardWrapper = styled.div<{ isMobile?: boolean, isDragMode?: boolean }>`
  background-color: var(--bg-list); color: var(--text-normal); padding: 1rem; border-radius: 8px;
  font-weight: bold; min-height: 100px; display: flex; flex-direction: column; justify-content: space-between;
  touch-action: ${props => props.isMobile ? (props.isDragMode ? 'none' : 'auto') : 'auto'};
  transition: background-color 0.2s ease-in-out;
  &:hover { background-color: var(--border-color); }
`;
const BoardName = styled.span`
  cursor: pointer;
  word-break: break-word;
`;
const BoardNameInput = styled(Input)`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;
const CardBottom = styled.div`
  align-self: flex-end;
`;
const NewBoardCard = styled(BoardCardWrapper)`
  background-color: var(--bg-list); color: var(--text-normal); align-items: center; justify-content: center;
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; background-color: var(--border-color); }
`;
const DeleteButton = styled.button`
  background: transparent; border: none; color: var(--text-dark); cursor: pointer;
  font-size: 1.2rem; line-height: 1; padding: 0.2rem 0.5rem;
  opacity: 0.7; border-radius: 4px;
  &:hover { opacity: 1; background-color: var(--danger-accent); color: white; }
`;
const DragModeToggleButton = styled.button<{ isActive: boolean }>`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${props => props.isActive ? 'var(--primary-accent)' : 'var(--bg-list)'};
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px var(--shadow-strong);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  z-index: 5;

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    background-color: ${props => props.isActive ? 'var(--primary-accent-hover)' : 'var(--border-color)'};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

// --- Sortable Board 컴포넌트 ---
const SortableBoard: React.FC<{ board: Board; onSelect: (board: Board) => void; onDelete: (e: React.MouseEvent, boardId: string) => void; onUpdate: (id: string, name: string) => void; disabled?: boolean; isDragModeEnabled?: boolean; }> = ({ board, onSelect, onDelete, onUpdate, disabled, isDragModeEnabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: board.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const isMobile = useIsMobile();
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(board.name);

    const handleNameClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
    };

    const handleSave = () => {
        if (name.trim() && name !== board.name) {
            onUpdate(board.id, name);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') {
            setName(board.name);
            setIsEditing(false);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(e, board.id);
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <BoardCardWrapper onClick={() => onSelect(board)} isMobile={isMobile} isDragMode={isDragModeEnabled}>
                {isEditing ? (
                    <BoardNameInput
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleSave}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                ) : (
                    <BoardName onClick={handleNameClick}>{board.name}</BoardName>
                )}
                <CardBottom>
                    <DeleteButton onClick={handleDeleteClick}>×</DeleteButton>
                </CardBottom>
            </BoardCardWrapper>
        </div>
    );
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return isMobile;
};

const BoardList: React.FC<{ onSelectBoard: (board: Board) => void; }> = ({ onSelectBoard }) => {
    const [boards, setBoards] = useState<Board[]>([]);
    const [newBoardName, setNewBoardName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isDragModeEnabled, setIsDragModeEnabled] = useState(false);
    const isMobile = useIsMobile();
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: isMobile && !isDragModeEnabled ? 999 : 5 }
    }));
    const fetchBoards = useCallback(async () => {
        try {
            const response = await getBoardList();
            setBoards(response.data?._embedded?.getList || []);
        } catch (error) { console.error('Failed to fetch boards', error); setBoards([]); }
    }, []);

    useEffect(() => { fetchBoards(); }, [fetchBoards]);

    const handleUpdateBoardName = async (id: string, name: string) => {
        try {
            await updateBoard({ id, name });
            setBoards(prev => prev.map(b => b.id === id ? { ...b, name } : b));
        } catch (error) {
            console.error('Failed to update board name', error);
            fetchBoards();
        }
    };

    const handleCreateBoard = async () => {
        if (!newBoardName.trim()) return;
        try {
            await createBoard({ name: newBoardName, priority: 0 });
            setNewBoardName('');
            setIsCreating(false);
            fetchBoards();
        } catch (error) { console.error('Failed to create board', error); }
    };

    const handleDeleteBoard = async (e: React.MouseEvent, boardId: string) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this board?')) {
            try {
                await deleteBoard(boardId);
                fetchBoards();
            } catch (error) {
                console.error('Failed to delete board', error);
                alert('Failed to delete board.');
            }
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = boards.findIndex(b => b.id === active.id);
        const newIndex = boards.findIndex(b => b.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const reorderedBoards = arrayMove(boards, oldIndex, newIndex);
        setBoards(reorderedBoards);

        const updatePromises = reorderedBoards.map((board, index) =>
            updateBoard({ id: board.id, priority: index })
        );
        try {
            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Failed to update board priorities', error);
            fetchBoards();
        }
    };

    return (
        <Container>
            <Title>My Boards</Title>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={boards.map(b => b.id)} strategy={rectSortingStrategy}>
                    <BoardGrid>
                        {boards.map((board) => (
                            <SortableBoard
                                key={board.id}
                                board={board}
                                onSelect={onSelectBoard}
                                onDelete={handleDeleteBoard}
                                onUpdate={handleUpdateBoardName}
                                disabled={isMobile && !isDragModeEnabled}
                                isDragModeEnabled={isDragModeEnabled}
                            />
                        ))}
                        {isCreating ? (
                            <div>
                                <Input
                                    placeholder="Enter board name..."
                                    value={newBoardName}
                                    onChange={(e) => setNewBoardName(e.target.value)}
                                    autoFocus
                                />
                                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                                    <Button onClick={handleCreateBoard}>Create Board</Button>
                                    <Button onClick={() => setIsCreating(false)} style={{ background: '#ccc' }}>Cancel</Button>
                                </div>
                            </div>
                        ) : (
                            <NewBoardCard onClick={() => setIsCreating(true)}>
                                <span>Create new board</span>
                            </NewBoardCard>
                        )}
                    </BoardGrid>
                </SortableContext>
            </DndContext>

            <DragModeToggleButton
                isActive={isDragModeEnabled}
                onClick={() => setIsDragModeEnabled(prev => !prev)}
                title={isDragModeEnabled ? "Switch to Scroll Mode" : "Switch to Drag Mode"}
            >
                {isDragModeEnabled ? (
                    // 드래그 모드 활성화 시 (스크롤 모드로 돌아가는 아이콘)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                ) : (
                    // 스크롤 모드 활성화 시 (드래그 모드로 전환하는 아이콘)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
                    </svg>
                )}
            </DragModeToggleButton>
        </Container>
    );
};

export default BoardList;

